/**
 * .tuMetrics {"tu1": 0.95, "tu2": 0.96, "tu3": 1, "notes": "Plan family: latest vs paramount version selection", "tuVersion": "2.5"}
 * .help "Pick which version of a plan family the list speaks for (paramount) vs which is newest for planning ahead."
 */
import type { Plan } from '@/types';

export const versionRank = (p: Pick<Plan, 'major_version' | 'minor_version' | 'patch_version'>) =>
  (p.major_version ?? 0) * 1_000_000 + (p.minor_version ?? 0) * 1_000 + (p.patch_version ?? 0);

/** Project this plan version is cut from (related_entity or settings). */
export function planProjectId(plan: Plan): string | null {
  if (plan.related_entity_type === 'project' && plan.related_entity_id) {
    return plan.related_entity_id;
  }
  const ids = plan.plan_settings?.project_ids;
  if (Array.isArray(ids) && typeof ids[0] === 'string') return ids[0];
  if (typeof plan.plan_settings?.project_id === 'string') return plan.plan_settings.project_id;
  return null;
}

const better = (a: Plan, b: Plan) => (versionRank(a) >= versionRank(b) ? a : b);

/** Newest by major.minor.patch (drafts included). */
export function pickLatestVersion(versions: Plan[]): Plan {
  if (!versions.length) throw new Error('pickLatestVersion: empty family');
  return versions.reduce(better);
}

/**
 * List face: executing beats complete beats newest draft.
 * While 1.0 is executing and 1.1 is draft, paramount is 1.0.
 */
export function pickParamountVersion(versions: Plan[]): Plan {
  if (!versions.length) throw new Error('pickParamountVersion: empty family');
  const executing = versions.filter((v) => v.version_state === 'executing');
  if (executing.length) return executing.reduce(better);
  const complete = versions.filter((v) => v.version_state === 'complete');
  if (complete.length) return complete.reduce(better);
  return versions.reduce(better);
}

/**
 * Existing editable row for the pencil, if any. When the plan of record is
 * locked and a next draft already exists, return that draft. Card tap uses
 * paramount separately. If locked with no draft yet, see pencilOpenForFamily.
 */
export function editTargetForFamily(paramount: Plan, latest: Plan): Plan {
  if (
    latest.version_state === 'draft' &&
    latest.id !== paramount.id
  ) {
    return latest;
  }
  return paramount;
}

/** Next minor cut after a locked plan of record (1.0 → 1.1). */
export function nextDraftVersion(paramount: Pick<Plan, 'major_version' | 'minor_version'>): {
  major: number;
  minor: number;
} {
  const major = (paramount.major_version ?? 0) === 0 ? 1 : (paramount.major_version ?? 1);
  return { major, minor: (paramount.minor_version ?? 0) + 1 };
}

export type PencilOpen = {
  plan: Plan;
  /** No saved draft ahead — open modal on the locked cut and jump to pending next draft. */
  openNextDraft: boolean;
};

/**
 * Pencil from the list: open an existing draft ahead, or start the next draft
 * when the live cut is locked (executing/complete) with nothing ahead yet.
 */
export function pencilOpenForFamily(paramount: Plan, latest: Plan): PencilOpen {
  const existing = editTargetForFamily(paramount, latest);
  if (existing.id !== paramount.id) {
    return { plan: existing, openNextDraft: false };
  }
  if (paramount.version_state === 'executing' || paramount.version_state === 'complete') {
    return { plan: paramount, openNextDraft: true };
  }
  return { plan: paramount, openNextDraft: false };
}

/** Collapse a flat plans result into root rows with latest + paramount. */
export function groupPlanFamilies(rows: Plan[]): Plan[] {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const roots: Plan[] = [];
  const versionsByRoot = new Map<string, Plan[]>();

  for (const row of rows) {
    const rootId = row.parent_version_id && byId.has(row.parent_version_id) ? row.parent_version_id : row.id;
    if (rootId === row.id) roots.push(row);
    const list = versionsByRoot.get(rootId) ?? [];
    list.push(row);
    versionsByRoot.set(rootId, list);
  }

  return roots.map((root) => {
    const versions = versionsByRoot.get(root.id) ?? [root];
    const latest = pickLatestVersion(versions);
    const paramount = pickParamountVersion(versions);
    return { ...root, latest_version: latest, paramount_version: paramount };
  });
}
